import React, { memo } from "react";
import { IconLoader2 } from "@tabler/icons-react";
import { Link } from "react-router-dom";

type IconButtonProps = {
  icon: React.ElementType;
  text: string;
  color?: string;
  padding?: string;
  onClickAction?: () => void;
  onHoverAction?: () => void;
  href?: string;
  align?: string;
  space?: string;
  active?: boolean;
  iconSize?: number;
  margin?: string;
  loading?: boolean;
  disabled?: boolean;
  disableWithoutShowing?: boolean;
  backgroundColor?: string;
  fontSize?: string;
  border?: string;
  width?: string;
  hoverColor?: string;
  textClassName?: string;
  testId?: string;
  /** Icon-only mode (P8): no label text is rendered — `ariaLabel` becomes
   *  mandatory for screen readers. Width defaults to w-max. */
  iconOnly?: boolean;
  /**
   * Collapse to the icon alone below `sm`, keeping the label from `sm` up.
   * Lets an action row of ~1000px of German labels fit one line on a phone
   * instead of wrapping into four.
   *
   * ⚠ The hidden label does NOT serve as the accessible name: per W3C Accessible
   * Name Computation §4.3.2 a `display:none` node is excluded from name-from-
   * content, and the Tabler icons carry neither `<title>` nor `aria-hidden`. The
   * button would therefore be nameless below `sm`. This mode falls back to
   * `aria-label={text}` when no explicit `ariaLabel` is given — name-based test
   * queries keep working either way, since jsdom applies no Tailwind.
   *
   * ⚠ Pair it with a `gap-*` value for `space`, not `space-x-*`: Tailwind v4's
   * space-x targets `:not(:last-child)`, so the icon would keep its trailing
   * margin once the label is display:none and the button would sit off-centre.
   */
  hideLabelBelowSm?: boolean;
  ariaLabel?: string;
  /** `aria-busy` on the button / link — for work in flight that keeps the
   *  button enabled (a spinner alone says nothing to assistive tech).
   *  Omitted = no attribute at all. */
  ariaBusy?: boolean;
  /**
   * Shown disabled but still focusable (SMS manual send, F3-14): the button
   * keeps its tab stop and says `aria-disabled`, a click does nothing, and the
   * reason — a hover/focus hint around it — stays reachable from the keyboard.
   * A natively `disabled` button drops out of the tab order and takes its
   * reason with it. Absent = today's behaviour.
   */
  softDisabled?: boolean;
  /** `aria-describedby` on the button (e.g. the hidden copy of a soft-disabled reason). */
  ariaDescribedBy?: string;
};

const IconButtonInner: React.FC<IconButtonProps> = ({
  icon: Icon,
  text,
  padding = "p-3",
  color = "font-tertiary",
  onClickAction,
  onHoverAction,
  href,
  align = "",
  space = "space-x-3",
  active = false,
  iconSize = 20,
  margin = "",
  loading = false,
  disabled = false,
  disableWithoutShowing = false,
  backgroundColor = "",
  fontSize = "body-2-medium",
  border = "",
  width = "w-full",
  hoverColor = "surface-secondary",
  textClassName = "",
  testId,
  iconOnly = false,
  hideLabelBelowSm = false,
  ariaLabel,
  ariaBusy,
  softDisabled = false,
  ariaDescribedBy,
}) => {
  // Soft-disabled looks disabled and answers no click, but stays a real,
  // focusable button (no `disabled` attribute).
  const isDisabled = disabled || loading || softDisabled;

  // See hideLabelBelowSm: a display:none label contributes no accessible name.
  const effectiveAriaLabel =
    ariaLabel ?? (hideLabelBelowSm || iconOnly ? text : undefined);

  const effectiveWidth = iconOnly && width === "w-full" ? "w-max" : width;
  const className = `flex flex-row items-center ${effectiveWidth} ${border} ${backgroundColor} ${margin} ${align} ${iconOnly ? "" : space} rounded-lg ${padding} ${
    active ? "surface-secondary" : ""
  } ${isDisabled ? "opacity-50" : `hover:${hoverColor}`} ${
    iconOnly && !isDisabled ? "cursor-pointer" : ""
  }`;

  const content = (
    <>
      {loading ? (
        <IconLoader2
          className={`${color} animate-spin ${iconOnly ? "" : hideLabelBelowSm ? "sm:mr-2" : "mr-2"}`}
          size={20}
        />
      ) : (
        <Icon
          size={iconSize}
          className={`${color} shrink-0 ${isDisabled ? "opacity-50" : ""}`}
        />
      )}
      {!iconOnly && (
        <p
          className={`${hideLabelBelowSm ? "hidden sm:block " : ""}${fontSize} ${color} ${textClassName} ${isDisabled ? "opacity-50" : ""}`}
        >
          {text}
        </p>
      )}
    </>
  );

  if (href && !isDisabled && !disableWithoutShowing) {
    return (
      <Link
        to={href}
        className={className}
        data-testid={testId}
        aria-label={effectiveAriaLabel}
        aria-busy={ariaBusy}
        onClick={(e) => {
          if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
          e.preventDefault();
          onClickAction?.();
        }}
        onMouseEnter={onHoverAction}
      >
        {content}
      </Link>
    );
  }

  return (
    <button
      className={className}
      data-testid={testId}
      aria-label={effectiveAriaLabel}
      aria-busy={ariaBusy}
      aria-disabled={softDisabled && !disabled && !loading ? true : undefined}
      aria-describedby={ariaDescribedBy}
      onClick={!isDisabled ? onClickAction : undefined}
      disabled={(isDisabled && !(softDisabled && !disabled && !loading)) || disableWithoutShowing}
      onMouseEnter={!isDisabled ? onHoverAction : undefined}
    >
      {content}
    </button>
  );
};

export const IconButton = memo(IconButtonInner);
